/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 02/07/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import './viewAuditFilterForm.css'

/**
 * HOC for the partner form
 */
class ViewAuditFilterForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            outComesAuditFromDate: '',
            outComesAuditToDate: '',

            outComesAuditPartnerId: '',
            outComesAuditPartnerName: '',
            outComesAuditInstitutionId: '',
            outComesAuditInstitutionName: '',
            outComesAuditDataSource: '',
            outComesAuditMessageType: '',
            outComesAuditStatus: '',
            outComesAuditCourseId: '',
            outComesAuditError: '',
            outComesAuditTransactionId: '',

            errors: {},

            // form fields
            fields: [
                {
                    inputType: 'dropdown',
                    labelName: 'Partner',
                    inputName: 'partnerId',
                    optionValueName: 'outComesAuditPartnerId',
                    optionLabelName: 'outComesAuditPartnerName',
                    required: true,
                    path: '/view-audit',
                    valueType: 'gradeBookPartners'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Institution',
                    inputName: 'outComesAuditInstitutionName',
                    optionValueName: 'outComesAuditInstitutionId',
                    optionLabelName: 'outComesAuditInstitutionName',
                    required: false,
                    path: '/view-audit',
                    valueType: 'gradeBookInstitutions'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Platform',
                    inputName: 'outComesAuditDataSource',
                    optionValueName: 'outComesAuditDataSource',
                    optionLabelName: 'outComesAuditDataSource',
                    required: false,
                    path: '/view-audit',
                    valueType: 'platforms'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Type',
                    inputName: 'outComesAuditMessageType',
                    optionValueName: 'outComesAuditMessageType',
                    optionLabelName: 'outComesAuditMessageType',
                    required: false,
                    path: '',
                    valueType: 'view-audit-type'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Status',
                    inputName: 'outComesAuditStatus',
                    optionValueName: 'outComesAuditStatus',
                    optionLabelName: 'outComesAuditStatus',
                    required: false,
                    path: '',
                    valueType: 'view-audit-status'
                },
                {
                    inputType: 'text',
                    labelName: 'CourseId',
                    inputName: 'outComesAuditCourseId',
                    optionValueName: 'outComesAuditCourseId',
                    optionLabelName: 'outComesAuditCourseId',
                    placeholder: 'Please enter Course ID here',
                    required: false,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Error/Action',
                    inputName: 'outComesAuditError',
                    optionValueName: 'outComesAuditError',
                    optionLabelName: 'outComesAuditError',
                    placeholder: 'Please enter error/action here',
                    required: false,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Transaction Id',
                    inputName: 'outComesAuditTransactionId',
                    optionValueName: 'outComesAuditTransactionId',
                    optionLabelName: 'outComesAuditTransactionId',
                    placeholder: 'Please enter transaction ID here',
                    required: false,
                    disabled: false
                },
                {
                    inputType: 'dateTime',
                    labelName: 'From',
                    inputName: 'outComesAuditFromDate',
                    optionValueName: 'outComesAuditFromDate',
                    optionLabelName: 'outComesAuditFromDate',
                    valueType: 'startDate'
                },
                {
                    inputType: 'dateTime',
                    labelName: 'To',
                    inputName: 'outComesAuditToDate',
                    optionValueName: 'outComesAuditToDate',
                    optionLabelName: 'outComesAuditToDate',
                    valueType: 'endDate'
                }
            ]
        };
        EventEmitter.emit('setPageHeaderData', {
            page: 'view-audit',
            pageName: "View Audit",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/view-audit",
            optionalIcons: ['manageColumnsIcon']
        });
    }

    /**
     * update the state when user inout a value
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionLabelName]: event.label,
                [event.optionValueName]: event.value
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            });
        }
    };

    loadFilteredResults = () => {
        let search = '&search=%7B'

        // get required fields
        for (const [key, value] of Object.entries(this.state)) {
            switch (key) {
                case 'outComesAuditPartnerName':
                case 'outComesAuditInstitutionName':
                case 'outComesAuditDataSource':
                case 'outComesAuditMessageType':
                case 'outComesAuditStatus':
                case 'outComesAuditCourseId':
                case 'outComesAuditError':
                case 'outComesAuditTransactionId':
                case 'outComesAuditFromDate':
                case 'outComesAuditToDate':
                    if ((typeof value === "string") && value !== '') {
                        search = search.concat(key,':',value,'%2C')
                    }
                    break;
                default:
            }
        }

        // remove the last %2C and add to the url
        if (search.length === 11) {
            search = '';
        } else if (search.length > 10) {
            search = search.slice(0, -3).concat('%7D')
        } else {
            search = '';
        }

        // publish the url to the table component
        EventEmitter.emit('tableFilterUrl', search)
        EventEmitter.emit('openNav', '')
    };

    clearFilteredResults = () => {
        const newState = Object.assign({}, this.state);
        newState.outComesAuditFromDate = '';
        newState.outComesAuditToDate = '';

        newState.outComesAuditPartnerId = '';
        newState.outComesAuditPartnerName = '';
        newState.outComesAuditInstitutionId = '';
        newState.outComesAuditInstitutionName = '';
        newState.outComesAuditDataSource = '';
        newState.outComesAuditMessageType = '';
        newState.outComesAuditStatus = '';
        newState.outComesAuditCourseId = '';
        newState.outComesAuditError = '';
        newState.outComesAuditTransactionId = '';

        newState.errors = {};

        this.setState(newState);

        EventEmitter.emit('tableFilterUrl', '')
        EventEmitter.emit('openNav', '')
    };

    /**
     * fire alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    render() {
        return(
            <div className="view-audit-filter-container">
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
                <div className="view-audit-filter-button-container">
                    <input type="button" className="view-audit-filter-cancel" value="Clear"
                           onClick={() => this.clearFilteredResults()} />
                    <input type="button" className="view-audit-filter-submit" value="Filter"
                           onClick={() => this.loadFilteredResults()}/>
                </div>
            </div>
        )
    }
}

export default ViewAuditFilterForm;
